import { useState } from "react";
import { SITE, whatsappLink } from "@/config/site";
import { MessageCircle, Mail, MapPin, BadgeCheck, Send } from "lucide-react";
import { toast, Toaster } from "sonner";

const CONTACTS = [
  {
    icon: MessageCircle,
    label: "WhatsApp",
    value: SITE.whatsappDisplay,
    href: whatsappLink(),
    testid: "contact-whatsapp",
  },
  {
    icon: Mail,
    label: "E-mail",
    value: SITE.email,
    href: `mailto:${SITE.email}`,
    testid: "contact-email",
  },
  {
    icon: BadgeCheck,
    label: "Registro Profissional",
    value: SITE.cro,
    href: null,
    testid: "contact-cro",
  },
  {
    icon: MapPin,
    label: "Atendimento",
    value: SITE.city,
    href: null,
    testid: "contact-location",
  },
];

export const Contact = () => {
  const [form, setForm] = useState({ nome: "", email: "", mensagem: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    const { nome, email, mensagem } = form;
    if (!nome.trim() || !email.trim() || !mensagem.trim()) {
      toast.error("Por favor, preencha todos os campos.");
      return;
    }
    // Redirect to WhatsApp with formatted message
    const text = encodeURIComponent(
      `Olá Dr. Sandrey, sou ${nome} (${email}).\n\n${mensagem}`,
    );
    window.open(
      `https://wa.me/${SITE.whatsappNumber}?text=${text}`,
      "_blank",
      "noopener,noreferrer",
    );
    toast.success("Redirecionando para o WhatsApp...");
    setForm({ nome: "", email: "", mensagem: "" });
  };

  return (
    <section
      id="contato"
      data-testid="contact-section"
      className="relative py-24 md:py-32 bg-[#050505] border-t border-[#D4AF37]/15"
    >
      <Toaster
        theme="dark"
        position="top-center"
        toastOptions={{
          style: {
            background: "#0a0a0a",
            border: "1px solid rgba(212, 175, 55, 0.3)",
            color: "#F8F8F8",
            borderRadius: 0,
          },
        }}
      />
      <div className="w-full max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left: heading + contacts */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="text-xs tracking-[0.32em] uppercase text-[#D4AF37]">
                Contato
              </span>
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-tight tracking-tight">
              Solicite uma{" "}
              <span className="italic text-[#D4AF37]">avaliação</span>.
            </h2>
            <p className="mt-6 text-white/65 leading-relaxed">
              Atendimento para perícias judiciais, assistência técnica,
              elaboração de pareceres, avaliação de danos, odontologia legal,
              implantodontia, ortodontia e endodontia. Todas as solicitações
              são tratadas com confidencialidade.
            </p>

            {/* Specialist portrait */}
            <div className="mt-10 relative overflow-hidden border border-[#D4AF37]/20">
              <img
                src="https://customer-assets.emergentagent.com/job_judicial-dental/artifacts/w7a2ze9s_WhatsApp%20Image%202026-06-03%20at%2019.18.21-2.jpeg"
                alt="Dr. Sandrey Miranda Nobre"
                className="w-full h-auto object-cover hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent p-6">
                <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-1">
                  Atendimento Pessoal
                </div>
                <div className="font-serif-display text-lg text-white">
                  Dr. Sandrey Miranda Nobre
                </div>
              </div>
            </div>

            <div className="mt-8 space-y-px bg-[#D4AF37]/10">
              {CONTACTS.map((c, i) => {
                const Icon = c.icon;
                const Inner = (
                  <div className="flex items-center gap-5 p-5 bg-[#0a0a0a] hover:bg-[#111111] transition-colors duration-300 group">
                    <div className="w-10 h-10 border border-[#D4AF37]/30 flex items-center justify-center shrink-0 group-hover:border-[#D4AF37] transition-colors">
                      <Icon
                        size={18}
                        strokeWidth={1.4}
                        className="text-[#D4AF37]"
                      />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]/80">
                        {c.label}
                      </div>
                      <div className="text-white text-sm mt-1">{c.value}</div>
                    </div>
                  </div>
                );
                return c.href ? (
                  <a
                    key={i}
                    href={c.href}
                    target={c.href.startsWith("http") ? "_blank" : undefined}
                    rel={c.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    data-testid={c.testid}
                    className="block"
                  >
                    {Inner}
                  </a>
                ) : (
                  <div key={i} data-testid={c.testid}>
                    {Inner}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="bg-[#0a0a0a] border border-[#D4AF37]/15 p-8 sm:p-12"
            >
              <div className="space-y-8">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]/80 block mb-3">
                    Nome
                  </label>
                  <input
                    type="text"
                    value={form.nome}
                    onChange={(e) =>
                      setForm({ ...form, nome: e.target.value })
                    }
                    data-testid="contact-input-name"
                    placeholder="Como deseja ser chamado"
                    className="w-full bg-transparent border-b border-[#D4AF37]/30 focus:border-[#D4AF37] outline-none py-3 text-white placeholder:text-white/30 transition-colors duration-300"
                  />
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]/80 block mb-3">
                    E-mail
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                    data-testid="contact-input-email"
                    placeholder="seu@email.com"
                    className="w-full bg-transparent border-b border-[#D4AF37]/30 focus:border-[#D4AF37] outline-none py-3 text-white placeholder:text-white/30 transition-colors duration-300"
                  />
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]/80 block mb-3">
                    Sua mensagem
                  </label>
                  <textarea
                    rows={5}
                    value={form.mensagem}
                    onChange={(e) =>
                      setForm({ ...form, mensagem: e.target.value })
                    }
                    data-testid="contact-input-message"
                    placeholder="Descreva brevemente o caso ou a dúvida"
                    className="w-full bg-transparent border-b border-[#D4AF37]/30 focus:border-[#D4AF37] outline-none py-3 text-white placeholder:text-white/30 transition-colors duration-300 resize-none"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-form-submit"
                  className="group inline-flex items-center gap-3 bg-[#D4AF37] text-[#050505] font-medium px-8 py-4 hover:bg-[#F0C950] transition-colors duration-300"
                >
                  Enviar via WhatsApp
                  <Send
                    size={16}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
